// Ticket.ts
// Represents a support ticket, including status, author, and details.
// Used as the core data model for support requests.

import {User} from "./User";
import {Rule} from "./Rule";
import {TicketStatus} from "shared/TicketStatus";
import {Group} from "shared/Group";
import {ReportType} from "./ReportType";
import {ReportStatus} from "./ReportStatus";
import {PenaltyBlockSelection} from "./PenaltyBlockSelection";

export enum TicketType {
    SUPPORT,
    REPORT
}

export enum ReportResolveType {
    UNRESOLVED,
    AGREEMENT,
    ACCEPT_GUILT,
    VALID,
    INVALID
}

export enum SuspectType {
    NONE = 0,
    REPORTED = 1,
    SUSPECT = 2
}

/**
 * Represents a support ticket, including status, author, and details.
 * Used as the core data model for support requests.
 */
export interface Ticket {
    /** Unique identifier for the ticket */
    _id: string
    /** Group associated with the ticket */
    group: string
    /** Category of the ticket */
    category: string
    /** User who created the ticket */
    createdBy: User
    /** Title of the ticket */
    title: string
    /** Status of the ticket (e.g., open, closed) */
    status: TicketStatus
    /** Timestamp when the ticket was created */
    timestamp: number
    /** List of users assigned to the ticket */
    assignees: User[]
    /** Roles assigned to the ticket */
    roleAssignees: string[]
    /** Per-user count of comments read. Key = user visualId; value = number of comments read (index of last read). Unread = commentsCount - lastReads[userId]. */
    lastReads: { [key: string]: number }
    /** Count of comments on the ticket */
    commentsCount: number
    /** Custom input fields for the ticket */
    inputFields: { [key: string]: string }
    /** Timestamp of the last comment */
    lastComment: number
    /** Type of the ticket (support or report) */
    type: TicketType
    /** Date when the report was made */
    reportDate: number
    /** User accused in the report, if any */
    reportAccused?: User
    /** Rules broken, if the ticket is a report */
    brokenRules: Rule[]
    /** Description of the ticket */
    description: string
    /** Attachments related to the ticket */
    attachments: string[]
    /** Partners involved in the report, if any */
    reportPartners: User[]
    /** Secondary rules applicable to the ticket */
    secondaryRules: Rule[]
    /** Indicates if the ticket is in fraganti (caught in the act) */
    inFraganti: boolean
    /** Resolution type for the report, if any */
    resolveType: ReportResolveType
    /** Indicates if the ticket has been reviewed */
    reviewed: boolean
    /** True while a report is awaiting staff approval before being released to the accused (protected-user gate) */
    pendingStaffApproval?: boolean
    /** True if staff rejected the report during the protected-user approval gate */
    approvalRejected?: boolean
    /** Epoch ms of the first visible (non staff-only) message posted by staff. Not backfilled for older tickets. */
    firstResponseAt?: number
    /** Epoch ms of the last close. Cleared on reopen. Not backfilled for older tickets. */
    closedAt?: number
    /** Epoch ms of the idle auto-close warning (IDLE_AUTOCLOSE) posted for the current idle period */
    idleWarningAt?: number
    
    // New fields for enhanced reporting system (all optional for backward compatibility)
    /** Report type: NORMAL or JURY_BASED (only for enhanced reports) */
    reportType?: ReportType;
    /** Current state in report lifecycle (only for enhanced reports) */
    reportStatus?: ReportStatus;
    /** User-selected penalty blocks when creating report */
    suggestedPenaltyBlocks?: PenaltyBlockSelection[];
    /** Staff-selected final penalty blocks */
    finalPenaltyBlocks?: PenaltyBlockSelection[];
    /** Agreed blocks from conciliation */
    agreedPenaltyBlocks?: PenaltyBlockSelection[];
    /** Percentage reduction for acceptance (e.g., 0.5 = 50%) */
    penaltyReduction?: number;
    
    // Deadlines
    /** Timestamp for accept/dispute deadline */
    responseDeadline?: number;
    /** Timestamp for conciliation end */
    conciliationDeadline?: number;
    /** Timestamp for jury voting end */
    juryVotingDeadline?: number;
    /** Timestamp when jury selection (JURY_PHASE) times out and escalates to staff review */
    jurySelectionDeadline?: number;
    /** Timestamp when an AGREEMENT_REACHED report is auto-closed with the agreed penalties */
    agreementCloseDeadline?: number;
    /** `${kind}:${deadline}` of deadline warnings already sent (server-only bookkeeping) */
    deadlineWarningsSent?: string[];
    
    // Conciliation
    /** Timestamp when conciliation started */
    conciliationStarted?: number;
    /** Agreement text if reached */
    conciliationAgreement?: string;
    /** ConciliationBlockTrade._id that was accepted */
    acceptedBlockTrade?: string;
    
    // NEW: Turn-based trading fields
    /** The ONE active trade that needs a response (only one at a time!) */
    activeTradeId?: string;
    /** Whose turn it is to respond (userId) - matches awaitingResponseFrom in active trade */
    tradingTurn?: string;
    /** History of all trades for audit */
    tradeHistory?: string[];
    /** Proposal counts per party (for limiting proposals) */
    reportedPartyProposalCount?: number;
    reporterProposalCount?: number;
    /** Response deadline for the current active proposal (timestamp) */
    proposalResponseDeadline?: number;
    
    // Jury
    /** Assigned jury members */
    juryMembers?: User[];
    /** Map of userId -> vote (true = valid, false = invalid) */
    juryVotes?: {
        [userId: string]: boolean;
    };
    /** Vote counts */
    juryVoteCount?: {
        valid: number;
        invalid: number;
    };
    /** True while auto-assign is calling the game jury candidates aggregation */
    jurySelectionInProgress?: boolean;
    /** Epoch ms when the current (or last) selection claim started — used for stale-lock reclaim */
    jurySelectionStartedAt?: number;
    /** Last auto-assign failure message; staff-only (stripped for parties) */
    jurySelectionLastError?: string | null;

    // Post-jury party choice + staff multi-vote (3rd instance)
    /**
     * True when jury voting ended without a verdict (deadline hit with no majority or
     * fewer than `minVotesRequired` votes). Status stays JURY_INVALID (no sanction by
     * default) but the UI shows "no verdict" and either party may escalate to staff.
     */
    juryNoVerdict?: boolean;
    /** Party choice after jury: accept verdict or appeal to staff */
    postJuryChoice?: 'accept' | 'appeal';
    /** User id who made the post-jury choice */
    postJuryChoiceBy?: string;
    /** Quantity multiplier applied at close (0.75 / 1.0 / 1.5) */
    penaltyMultiplier?: number;
    /** Auto-assigned staff reviewers (when STAFF_REVIEW_SETTINGS.mode = auto_assign) */
    staffReviewers?: User[];
    /** Map of staff userId -> vote (true = accept/convict, false = reject/invalid) */
    staffVotes?: {
        [userId: string]: boolean;
    };
    staffVoteCount?: {
        accept: number;
        reject: number;
    };
    /** True once accept or reject hit the configured threshold */
    staffVoteThresholdReached?: boolean;
    /** Deadline for post-jury party choice */
    postJuryChoiceDeadline?: number;
    /** Deadline for staff voting phase */
    staffVotingDeadline?: number;
    /** Classifier reroute hint for tickets filed in a catch-all category (staff-only, ENG-1759) */
    suggestedCategory?: SuggestedCategory;
    /** What the simple flow decided and how the ticket ended up (staff-only, ENG-1759 impact monitoring) */
    categoryDecision?: CategoryDecision;
}

export interface CategoryDecision {
    modelId: string | null
    auto: { group: string; category: string; score: number | null; confident: boolean } | null
    threshold: number | null
    thresholdMode: "fixed" | "auto" | null
    /** none: no automatic pick; auto: confident pick kept; fallback: unsure bucket kept; player: changed by the player */
    source: "none" | "auto" | "fallback" | "player"
    at: number
}

export interface SuggestedCategory {
    group: string
    category: string
    score: number
    /** "auto": raw top prediction kept at creation by the simple flows (ENG-1759); absent on reroute hints */
    source?: "auto"
}

export function isNotAssigned(ticket: Ticket): boolean {
    return ticket.assignees.length === 0 && ticket.roleAssignees.length === 0;
}

/**
 * Checks if a ticket is an enhanced report (has report-specific fields)
 * @param ticket The ticket to check
 * @returns true if ticket has reportStatus field (indicating enhanced report)
 */
export function isEnhancedReport(ticket: Ticket | null | undefined): boolean {
    return ticket != null && ticket.type === TicketType.REPORT && ticket.reportStatus !== undefined;
}

/**
 * Gets the effective status for a ticket (reportStatus if enhanced, else status)
 * @param ticket The ticket
 * @returns The effective status
 */
export function getEffectiveStatus(ticket: Ticket): TicketStatus | ReportStatus {
    if (isEnhancedReport(ticket) && ticket.reportStatus !== undefined) {
        return ticket.reportStatus;
    }
    return ticket.status;
}

/**
 * Gets the effective report type (reportType if enhanced, else defaults to NORMAL)
 * @param ticket The ticket
 * @returns The effective report type
 */
export function getEffectiveReportType(ticket: Ticket): ReportType {
    if (isEnhancedReport(ticket) && ticket.reportType !== undefined) {
        return ticket.reportType;
    }
    return ReportType.NORMAL;
}